import { mapGetters, mapActions } from 'vuex'
import Configuration from '@/agilis/util/Configuration'
import configurationMixin from '@/agilis/components/DataTableV2/common/configurationMixin'
import deleteMixin from '@/agilis/components/DataTableV2/common/deleteMixin'
import renderMixin from '@/agilis/components/DataTableV2/common/renderMixin'
import searchMixin from '@/agilis/components/DataTableV2/common/searchMixin'
import DataTableGroupingMixin from '@/agilis/components/DataTableWithGroups/common/DataTableGroupingMixin'
import L from 'lodash'
import Vue from 'vue'
import jsonLogic from '@/plugins/jsonLogic'

export default {
  name: 'DataTableWithGroups',
  mixins: [
    configurationMixin,
    deleteMixin,
    renderMixin,
    searchMixin,
    DataTableGroupingMixin
  ],

  props: {
    groupEditable: {
      type: Boolean,
      default: false
    },
    hideEmptyTable: {
      type: Boolean,
      default: false
    },
    groupApi: String,
    groups: {
      type: Array,
      default: () => ([])
    },
    configuration: Object,
    description: String,
    fetch: {
      type: Function,
      default: null
    },
    featureId: String,
    dataTableItems: {
      type: Array,
      default: () => ([])
    },
    itemsKey: String,
    title: String,
    trackerDTO: Object,
    footer: String
  },

  data () {
    return {
      groupDialog: false,
      expanded: true,
      sortBy: null,
      sortDesc: false,
      selectedSortValue: null,
      groupDTO: {}
    }
  },

  watch: {
    dataTableItems: {
      handler (newValue) {
        this.items = L.cloneDeep(newValue)
      }
    }
  },

  mounted: function () {
    this.items = L.cloneDeep(this.dataTableItems)
    this.setGroupingConfiguration(this.configuration)
    this.groupDTO = Configuration.datatable.getDTO(this.groupConfiguration)
  },

  computed: {
    ...mapGetters({
      'selectedContext': 'contextStore/selectedContext',
      'userId': 'userDataStore/userId'
    }),

    hasGroups () {
      return !!this.configuration.grouping
    },

    groupConfiguration () {
      return this.configuration.grouping?.group?.dataTable
    },

    groupId () {
      return this.configuration.grouping?.group?.groupId
    },

    groupFormRows () {
      return this.groupConfiguration?.formRows
    },

    groupFormField () {
      return L.cloneDeep(this.groupConfiguration?.columns)
    },

    groupFormTitle () {
      const title = this.groupDTO.id ? this.$t('label.editTrackerType', { trackerType: this.title }) : this.$t('label.createTrackerType', { trackerType: this.title })
      return Configuration.i18n.capitalizePhrase(title)
    },

    sortableColumns () {
      let sortByColumns = []
      if (this.configuration?.grouping?.sortByColumns) {
        const columns = this.configuration.grouping.sortByColumns
        sortByColumns = columns.map(col => {
          return { id: this.configuration.columns[col].id, name: this.$t(this.configuration.columns[col].label) }
        })
      }
      return sortByColumns
    }
  },

  methods: {
    // override ineherited of add new methods
    ...mapActions({
      notifyError: 'notificationStore/notifyError',
      startLoader: 'loaderStore/startLoader',
      stopLoader: 'loaderStore/stopLoader'
    }),

    expandAllGroups () {
      this.expanded = !this.expanded
      const groupsObjKeys = Object.keys(this.$refs.datatable.openCache)
      groupsObjKeys.map(key => {
        this.$refs.datatable.openCache[key] = this.expanded
      })
    },

    fetchData () {
      if (typeof this.fetch === 'function') {
        this.fetch()
      }
    },

    createGroup () {
      this.groupDTO = Configuration.datatable.getDTO(this.groupConfiguration)
    },

    async saveGroup (_group) {
      this.startLoader({ id: 'save-group' })

      setTimeout(async () => {
        let group = JSON.parse(JSON.stringify(_group)) // work on a copy of the object to persist data in case of errors
        group = this.addContext(group)

        this.startLoader({ id: 'save-group' })
        if (group.id) {
          const url = `${this.groupApi}/${group.id}`
          try {
            await this.$axios.put(url, group, this.$axios.agilisRequestConfig(url))
            this.fetch()
          } catch (error) {
            this.notifyError(error)
          }
          this.stopLoader({ id: 'save-group' })
          this.closeDialog()
        } else {
          const url = `${this.groupApi}`
          try {
            await this.$axios.post(url, group, this.$axios.agilisRequestConfig(url))
            this.fetch()
          } catch (error) {
            this.notifyError(error)
          }
          this.stopLoader({ id: 'save-group' })
          this.closeDialog()
        }
      }, 0)
    },

    groupTitle (items, group) {
      let itemGroup = items.find(item => item[this.groupingColumnId] === group)
      const decoratedJsonLogicContext = this.decorateJsonLogicContext(itemGroup)
      return jsonLogic.apply(this.configuration.grouping.title.jsonLogic, decoratedJsonLogicContext)
    },

    groupLength (items) {
      const validItems = items.filter(item => item[this.itemsKey])
      return validItems.length
    },

    viewGroup (items, group) {
      let item = items.find(item => item[this.groupingColumnId] === group)
      const url = `${this.groupApi}/${item[this.groupId]}`

      Vue.axios.get(url, Vue.axios.agilisRequestConfig(url)).then(response => {
        this.groupDTO = Object.assign(this.groupDTO, response.data)
        this.groupDialog = true
      }).catch(error => {
        this.notifyError(error)
      })
    },

    handleDeleteGroup (items, group) {
      if (this.groupLength(items) === 0) {
        this.deleteGroup(items, group)
        return
      }
      const userConfirmPromise = this.notifyUserConfirm({
        title: this.computedDeleteTitle,
        messages: [this.deleteMessage(this.$t('sales.label.wpGroup'))],
        confirm: this.deleteNo,
        cancel: this.deleteYes
      })

      userConfirmPromise.then((userResponse) => {
        if (!userResponse) {
          this.deleteGroup(items, group)
        }
      })
    },

    deleteGroup (items, group) {
      let item = items.find(item => item[this.groupingColumnId] === group)
      const url = `${this.groupApi}/${item[this.groupId]}`
      Vue.axios
        .delete(
          url,
          {
            ...Vue.axios.agilisRequestConfig(url)
          })
        .then(() => {
          this.fetch()
        })
        .catch((error) => {
          this.notifyError(error)
        })
    },

    closeDialog () {
      this.groupDialog = false
      this.groupDTO = {}
    },

    addContext (item) {
      return Configuration.addContext(this.selectedContext, item)
    },

    userCanDoAndAccessFeature (operation) {
      return this.featureId ? Configuration.auth.userCanDoAndAccessFeature(
        this.selectedContext, this.featureId, operation, this.trackerDTO, this.userId) : true
    }
  }
}
